// Style atoms and small presentational components shared by every employee-profile
// section (apps/web/src/components/employee-form/sections/*) and by EmployeeProfilePage
// itself. Kept dependency-free (no closures over page state) so any section can import
// them directly.

import { createContext, useContext, useEffect, useState } from "react";
import { ChevronRight, Eye, EyeOff } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";
import { Button } from "../ui/button.js";
import { GlowCard } from "../ui/glow-card.js";
import { useSectionCollapse } from "./SectionCollapseContext.js";
import { useSectionPrivacy } from "./SectionPrivacyContext.js";

export interface FieldAccess {
  canViewInProfile: "HIDDEN" | "MASKED" | "FULL";
  canEdit: "NONE" | "DIRECT" | "WITH_APPROVAL";
  isLocked: boolean;
  hasPendingChange: boolean;
  pendingValue: string | null;
  pendingAt: string | null;
}

export const inp: React.CSSProperties = {
  padding: "8px 10px",
  border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)",
  fontSize: "14px",
  width: "100%",
  background: "var(--color-surface-0)",
  outline: "none",
  boxSizing: "border-box",
};

export const disabledInp: React.CSSProperties = {
  ...inp,
  background: "var(--color-surface-50)",
  color: "var(--color-surface-600)",
  cursor: "not-allowed",
};

export const sel: React.CSSProperties = { ...inp, cursor: "pointer" };

export function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 break-words">
      <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-700)" }}>{label}</label>
      {children}
    </div>
  );
}

// The one responsive grid every profile section lays its fields out on.
//
// `auto`/`compact`/`wide` track floors are wrapped in `min(100%, …)` so a column
// can never be wider than the card itself -- a bare `minmax(200px, 1fr)` keeps a
// 200px column on a 170px-wide card and the fields spill out the right edge,
// which is what was dragging a horizontal scrollbar onto the page. The fixed
// `two`/`three` layouts stack to one column on small screens instead of
// squeezing every field into a sliver.
const fieldGridVariants = cva("grid [&>*]:min-w-0", {
  variants: {
    cols: {
      auto: "grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))]",
      compact: "grid-cols-[repeat(auto-fit,minmax(min(100%,150px),1fr))]",
      wide: "grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))]",
      two: "grid-cols-1 sm:grid-cols-2",
      three: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    },
    gap: { none: "gap-0", sm: "gap-3", md: "gap-4", lg: "gap-6" },
  },
  defaultVariants: { cols: "auto", gap: "md" },
});

export interface FieldGridProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof fieldGridVariants> {}

export function FieldGrid({ className, cols, gap, ...props }: FieldGridProps) {
  return <div className={cn(fieldGridVariants({ cols, gap }), className)} {...props} />;
}

// Every profile section card: a GlowCard shell, a header that toggles the body,
// and the divider that used to live in SectionHeader (moved onto the body so a
// collapsed card is a single clean row). `id` is the section nav's id, so the
// nav can open this card before scrolling to it.
/** Open/close duration, shared by the body's grid transition and its unmount timer. */
const SECTION_ANIM_MS = 300;

/** View-only profile: every section's body and header actions sit in a disabled
 *  fieldset, which natively disables all inputs, selects and buttons inside. The
 *  open/close toggle stays outside it so sections still expand individually. */
const SectionReadOnlyContext = createContext(false);
export const SectionReadOnlyProvider = SectionReadOnlyContext.Provider;
const READ_ONLY_FIELDSET = "m-0 min-w-0 border-0 p-0";

/** A hidden card blurs every element that isn't a field label (or doesn't wrap
 *  one), so "PAN Number" stays readable while the number itself doesn't. Clicks
 *  and selection are off too; the fieldset's `disabled` covers keyboard focus. */
const HIDDEN_VALUES = "pointer-events-none select-none [&_:not(label,label_*,:has(label))]:blur-[5px]";

export function CollapsibleSection({
  id, title, sectionRef, actions, style, children, editableWhenReadOnly = false,
}: {
  id: string;
  title: React.ReactNode;
  sectionRef?: React.RefObject<HTMLDivElement>;
  actions?: React.ReactNode;
  style?: React.CSSProperties;
  children: React.ReactNode;
  /**
   * Keeps this section interactive on a read-only profile, for a section that governs
   * its own permission rather than the page's. Work Schedule is the case: a manager
   * opens a team member's profile read-only but may still set their shift, which is
   * checked by the shift endpoint, not by the profile's edit rights.
   */
  editableWhenReadOnly?: boolean;
}) {
  const { isOpen, toggle } = useSectionCollapse();
  const open = isOpen(id);
  const readOnly = useContext(SectionReadOnlyContext) && !editableWhenReadOnly;
  const { isHidden, toggle: togglePrivacy } = useSectionPrivacy();
  const hidden = isHidden(id);

  // The body animates open and shut rather than appearing all at once, but a shut
  // section still unmounts its children -- several of them fetch on mount, and on
  // arrival every section is shut. `mounted` therefore trails `open`: it leads on
  // the way in (so there is something to animate) and lags on the way out (so the
  // closing animation has something to animate away).
  const [mounted, setMounted] = useState(open);
  const [expanded, setExpanded] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Next frame, so the row starts at 0fr and transitions to 1fr instead of
      // mounting already open.
      const frame = requestAnimationFrame(() => setExpanded(true));
      return () => cancelAnimationFrame(frame);
    }
    setExpanded(false);
    // A timer rather than transitionend: under reduced motion there is no
    // transition to end, and the children must still come back out.
    const timer = window.setTimeout(() => setMounted(false), SECTION_ANIM_MS);
    return () => clearTimeout(timer);
  }, [open]);
  return (
    // GlowCard at its mildest: an ultra-low border glow that follows the pointer
    // near an edge, and no interior hover light (spotlight="off"). size/radius/
    // border match the old `card` atom, so the surface itself looks unchanged.
    <GlowCard
      ref={sectionRef}
      data-section={id}
      glow="ultra"
      spotlight="off"
      size="lg"
      radius="lg"
      className="mb-4"
      style={style}
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => toggle(id)}
          aria-expanded={open}
          aria-controls={`section-body-${id}`}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left"
        >
          <ChevronRight
            size={15}
            className={cn("shrink-0 text-surface-400 transition-transform duration-300 ease-out motion-reduce:transition-none", open && "rotate-90")}
          />
          <h3 className="m-0 min-w-0 flex-1 text-[14px] font-bold text-surface-800">{title}</h3>
        </button>
        {actions && (readOnly ? <fieldset disabled className={READ_ONLY_FIELDSET}>{actions}</fieldset> : actions)}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-surface-500"
          onClick={() => togglePrivacy(id)}
          aria-pressed={hidden}
          aria-label={hidden ? "Show details" : "Hide details"}
          title={hidden ? "Show details" : "Hide details"}
        >
          {hidden ? <Eye size={15} /> : <EyeOff size={15} />}
        </Button>
      </div>
      {/* The 0fr/1fr grid row animates to the body's own height without measuring
          it. The wrapper stays mounted so the header's aria-controls always
          resolves, even while the section is shut. */}
      <div
        id={`section-body-${id}`}
        aria-hidden={!open}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          {mounted && (
            <div className={cn("mt-3 border-t border-surface-100 pt-4", hidden && HIDDEN_VALUES)} aria-hidden={hidden || undefined}>
              <fieldset disabled={readOnly || hidden} className={READ_ONLY_FIELDSET}>{children}</fieldset>
            </div>
          )}
        </div>
      </div>
    </GlowCard>
  );
}

export function PendingBadge({ value, date }: { value: string | null; date: string | null }) {
  if (!value) return null;
  return (
    // break-words: a pending value can be an id or a long free-text field.
    <div className="break-words" style={{
      marginTop: "4px", padding: "4px 8px",
      background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)",
      borderRadius: "var(--radius-sm)", fontSize: "11px", color: "var(--color-warning-fg)",
    }}>
      Pending approval: {value}
      {date && <span style={{ marginLeft: "6px", opacity: 0.7 }}>{new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>}
    </div>
  );
}
